import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { saveAuthSession, signupUser } from '../services/authApi';

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('authToken')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'First name is required.';
    if (!form.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address.';
    if (!form.password) errs.password = 'Password is required.';
    else if (form.password.length < 6) errs.password = 'Password must be at least 6 characters.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    setSubmitted(true);
    setApiError('');

    if (Object.keys(errs).length === 0) {
      setIsLoading(true);

      try {
        const planId = Number(searchParams.get('plan_id'));
        const authData = await signupUser({
          name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
          email: form.email.trim(),
          password: form.password,
          ...(Number.isInteger(planId) && planId > 0 ? { plan_id: planId } : {}),
        });

        saveAuthSession(authData);
        navigate('/my-account');
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const inputBase = "w-full bg-[#0F172A] border text-white rounded-xl pl-11 pr-4 py-3 focus:outline-none transition-all duration-300 shadow-inner";
  const inputOk = "border-slate-700/80 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB]/50";
  const inputErr = "border-red-500/60 focus:border-red-500/80 focus:ring-1 focus:ring-red-500/50";

  return (
    <div className="min-h-screen bg-[#0F172A] font-sans text-white  flex flex-col relative  " >
      
      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img src="https://images.unsplash.com/photo-1590283603385-18ff388834b5?auto=format&fit=crop&q=80&w=2400" alt="Background" className="w-full h-full object-cover opacity-[0.03] mix-blend-screen" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />
        
        {/* Theme Orbs */}
        <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-[#F43F5E]/15 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-50">
        <Header />
      </div>

      <main className="flex-1 relative z-10 flex items-center justify-center p-6 min-h-[85vh] pt-40 pb-16">
        <div className="w-full max-w-md bg-[#1E293B]/90 backdrop-blur-xl border border-slate-700/80 p-8 rounded-[2rem] shadow-2xl relative">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-extrabold mb-2 text-white">
              Create an Account
            </h1>
            <p className="text-slate-400 text-sm">
              Join <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E] font-semibold">FinanceSkillHub</span> to start your path to mastery.
            </p>
          </div>

          <form className="space-y-4 text-left" onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">First Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-slate-500" />
                  </div>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    className={`${inputBase} ${submitted && errors.firstName ? inputErr : inputOk}`}
                    placeholder="Alex"
                  />
                </div>
                {submitted && errors.firstName && <p className="text-red-400 text-xs mt-1.5">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Last Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-5 h-5 text-slate-500" />
                  </div>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    className={`${inputBase} ${submitted && errors.lastName ? inputErr : inputOk}`}
                    placeholder="Carter"
                  />
                </div>
                {submitted && errors.lastName && <p className="text-red-400 text-xs mt-1.5">{errors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-slate-500" />
                </div>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`${inputBase} ${submitted && errors.email ? inputErr : inputOk}`}
                  placeholder="investor@example.com"
                />
              </div>
              {submitted && errors.email && <p className="text-red-400 text-xs mt-1.5">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-slate-500" />
                </div>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`${inputBase} ${submitted && errors.password ? inputErr : inputOk}`}
                  placeholder="********"
                />
              </div>
              {submitted && errors.password && <p className="text-red-400 text-xs mt-1.5">{errors.password}</p>}
            </div>

            {apiError && <p className="text-red-400 text-xs">{apiError}</p>}

            <div className="pt-2">
              <button type="submit" disabled={isLoading} className="w-full text-center justify-center bg-[#2563EB] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#1d4ed8] hover:shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all text-sm flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed">
                {isLoading ? 'Creating Account...' : 'Create Account'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-sm text-slate-400">
            Already have an account? <Link to="/login" className="text-[#F43F5E] font-bold hover:text-[#E11D48] transition-colors">Log in</Link>
          </div>
        </div>
      </main>

      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}
